import { useEffect, useRef, useState } from 'react';

interface UseIdleLogoutOptions {
  /** Idle time before the logout countdown starts. Default 15 minutes. */
  idleMs?: number;
  /** Countdown length (seconds) shown before logout. Default 3. */
  countdownSeconds?: number;
  /** Gate — only arm the countdown when a session is actually active. */
  isActive: () => boolean;
  /** Called when the countdown reaches zero. */
  onLogout: () => void;
}

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

/**
 * Logs the user out after a period of inactivity. After `idleMs` with no user
 * activity it shows a short countdown (returned to the caller to render), then
 * calls `onLogout`. Activity resets the idle timer, but once the countdown has
 * started it runs to completion.
 *
 * Returns the current countdown value (seconds) while warning, else null.
 */
export function useIdleLogout({
  idleMs = 60 * 60 * 1000,
  countdownSeconds = 3,
  isActive,
  onLogout,
}: UseIdleLogoutOptions): number | null {
  const [countdown, setCountdown] = useState<number | null>(null);

  // Keep latest callbacks without re-arming the effect.
  const isActiveRef = useRef(isActive);
  const onLogoutRef = useRef(onLogout);
  isActiveRef.current = isActive;
  onLogoutRef.current = onLogout;

  useEffect(() => {
    let idleTimer: ReturnType<typeof setTimeout> | null = null;
    let countdownTimer: ReturnType<typeof setInterval> | null = null;
    let counting = false;

    const clearIdle = () => {
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = null;
    };
    const clearCountdown = () => {
      if (countdownTimer) clearInterval(countdownTimer);
      countdownTimer = null;
    };

    const armIdle = () => {
      clearIdle();
      idleTimer = setTimeout(beginCountdown, idleMs);
    };

    function beginCountdown() {
      // No active session → nothing to log out of; just keep watching.
      if (!isActiveRef.current()) {
        armIdle();
        return;
      }
      counting = true;
      let remaining = countdownSeconds;
      setCountdown(remaining);
      countdownTimer = setInterval(() => {
        remaining -= 1;
        if (remaining <= 0) {
          clearCountdown();
          counting = false;
          setCountdown(null);
          onLogoutRef.current();
        } else {
          setCountdown(remaining);
        }
      }, 1000);
    }

    const onActivity = () => {
      // Don't interrupt an in-progress countdown.
      if (counting) return;
      armIdle();
    };

    ACTIVITY_EVENTS.forEach((e) =>
      window.addEventListener(e, onActivity, { passive: true })
    );
    armIdle();

    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      clearIdle();
      clearCountdown();
    };
  }, [idleMs, countdownSeconds]);

  return countdown;
}
